import { computed, ref, watch, nextTick } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { useBuilder } from "./builder.store";
import { usePrimaryResumeStore } from "./primary-resume.store";

import { useIgnoreWarnings } from "./scorer-warnings.store";
import { setAuthState } from "../composables/auth";
import { update } from '../api/profile'

import {
    setBulletPointsScore,
    setFontFileSize,
    setMarginsSize,
    setRelevantInfoPersonalInfo,
    setRelevantInfoBp,
    setExperienceOrder,
    setSectionFormatting,
    setRelevantInfoExperienceDate,
    setExperience,
    getJobsAndBullets,
    setSkills,
    calculateKeywords,
    setEducationScore,
    setExperienceScore,
} from "./scorer-utils";

import { useBuilderStyles } from "./builder-style.store";
import { useOnboardingChecklist } from "./onboarding-checklist.store";

const { getUser, setUser } = setAuthState();


export const useResumeScorerStore = defineStore("resume-scorer", () => {
    const route = useRoute();
    const warningsStore = useIgnoreWarnings();
    const builderStore = useBuilder();
    const stylesStore = useBuilderStyles();
    const primaryResumeStore = usePrimaryResumeStore();

    const showResumeScorer = ref(false);
    const showBreakdownModal = ref(false);

    const { primaryResume } = storeToRefs(primaryResumeStore);
    const { textSize } = storeToRefs(stylesStore);
    const { bulletPointsWithId } = storeToRefs(builderStore);

    const resumeCopy = ref(null);
    const bulletsIdCopy = ref([]);

    const currentUser = ref(getUser());

    const textSizeCopy = ref(0);
    let lastResumeName = ref(null);
    let lastResumeContact = ref(null);
    let margins = ref([0, 0]);
    let nrOfPagesScorer = ref(0);

    let breakdown = ref({});

    // bullet points
    let informationBi = ref({});

    // relevant info
    let informationDates = ref({});
    let bulletInfo = ref({});
    let informationBp = ref({});
    let educationInfo = ref({});
    let experiencesInfo = ref({});

    // formatting
    let formattingSectionFormat = ref({});
    let formattingSectionOrder = ref({});
    let formattingMargins = ref({});
    let formattingFontFileSize = ref({});

    // skills
    let skillsNotPassedWarning = ref({});
    let skillsAddKeywords = ref({});
    let internSkillsJobWarning = ref({});
    let lastUsedJobsAndBullets = ref({ lastJobs: [], lastBullets: [] });

    // experience
    let experiencePopulation = ref({});

    let totalScore = ref(0);

    const setShowResumeScorer = () => {
        showResumeScorer.value = true;
    };

    const hideResumeScorer = () => {
        showResumeScorer.value = false;
    };

    const setShowBreakdown = () => {
        showBreakdownModal.value = true;
    };

    const hideBreakdown = () => {
        showBreakdownModal.value = false;
    };

    watch(route, () => {
        showResumeScorer.value = false;
    });

    const isOnResumePage = computed(() => {
        return route?.name === "resume.edit";
    });

    const getScore = computed(() => {
        if (isOnResumePage.value) {
            return null;
        }

        const stack = [breakdown.value];

        let bulletPointsScore = 0;
        let relevantInfoScore = 0;
        let formattingScore = 0;
        let skillsScore = breakdown?.value.skills?.score || 0;

        while (stack?.length > 0) {
            const currentItem = stack.pop();

            Object.values(currentItem).forEach((item) => {
                if (item.hasOwnProperty("score")) {
                    if (currentItem === breakdown.value.bulletPoints) {
                        bulletPointsScore += item.score;
                    } else if (
                        currentItem === breakdown.value.relevantInformation
                    ) {
                        relevantInfoScore += item.score;
                    } else if (currentItem === breakdown.value.formatting) {
                        formattingScore += item.score;
                    }
                } else if (typeof item === "object") {
                    stack.push(item);
                }
            });
        }

        const result = {
            bulletPointsScore: Math.ceil(bulletPointsScore) || 0,
            relevantInformationScore: Math.ceil(relevantInfoScore) || 0,
            formattingScore: Math.ceil(formattingScore) || 0,
            skillsScore: Math.ceil(skillsScore) || 0,
        };

        result["totalScore"] = Object.values(result).reduce(
            (totalScore, score) => (totalScore += Math.ceil(score))
        );

        totalScore.value = result["totalScore"];

        return result;
    });

    watch(
        totalScore,
        async (newScore, oldScore) => {
            if (primaryResume?.value?.uuid !== route?.params?.id) return;
            
            try {
                if (newScore >= 75) {
                    useOnboardingChecklist().markItemAsCompleted(1, "get75ResumeScore");
                }
            } catch (error) {
                console.error("Error updating job_search_blockers:", error);
            }
        },
        { immediate: true }
    );

    const getWarnings = computed(() => {
        if (isOnResumePage.value) {
            return [];
        }
        const resumeId = route.params.id;

        const result = [
            ...Object.values(informationBi.value).flat(),
            ...Object.values(bulletInfo.value).flat(),
            ...Object.values(informationBp.value).flat(),
            ...Object.values(formattingSectionFormat.value).flat(),
            ...Object.values(informationDates.value).flat(),
            ...Object.values(formattingSectionOrder.value).flat(),
            ...Object.values(formattingMargins.value).flat(),
            ...Object.values(formattingFontFileSize.value).flat(),
            ...Object.values(experiencePopulation.value).flat(),
            ...Object.values(skillsNotPassedWarning.value).flat(),
            ...Object.values(skillsAddKeywords.value).flat(),
            ...Object.values(educationInfo.value).flat(),
            ...Object.values(experiencesInfo.value).flat(),
            ...Object.values(internSkillsJobWarning.value).flat(),
        ].filter((warning) => {
            // TODO: implement filtering when warning is creted instead of at the end !! IMPORTANT !!
            return !warningsStore.warningIsIgnored(resumeId, warning.warningId);
        });

        return result;
    });

    const fontChangeHandler = () => {
        formattingFontFileSize.value = {};

        const result = setFontFileSize(
            nrOfPagesScorer.value,
            formattingFontFileSize.value,
            breakdown.value,
            textSize.value
        );

        breakdown.value = result.breakdownCopy;
        formattingFontFileSize.value = result.formatingFontFileSizeCopy;
    };

    const resumeContactChangeHandler = () => {
        if (resumeCopy == null) return;

        informationBi.value = {};
        lastResumeName.value = resumeCopy?.value.name;
        lastResumeContact.value = resumeCopy?.value.contact;

        const result = setRelevantInfoPersonalInfo(
            resumeCopy.value,
            breakdown.value,
            informationBi.value
        );

        breakdown.value = result.breakdown;
        informationBi.value = result.informationBi;
    };

    const bulletPointsChangeHandler = () => {
        bulletInfo.value = {};

        const result = setBulletPointsScore(
            bulletPointsWithId?.value,
            bulletInfo?.value,
            breakdown?.value
        );

        breakdown.value = result.breakdownCopy;
        bulletInfo.value = result.bulletInfo;
    };

    const relevantInfoExperienceChangeHandler = () => {
        informationDates.value = {};

        const result = setRelevantInfoExperienceDate(
            breakdown.value,
            resumeCopy.value,
            informationDates.value
        );

        breakdown.value = result.breakdownCopy;
        informationDates.value = result.informationDatesCopy;
    };

    const experienceOrderChangeHandler = () => {
        formattingSectionOrder.value = {};

        const result = setExperienceOrder(breakdown.value, resumeCopy.value);

        if (result == null) return;

        breakdown.value = result.newBreakdown;
        formattingSectionOrder.value = result.newFormattingSectionOrder;
    };

    const sectionsFormatingChangeHandler = () => {
        formattingSectionFormat.value = {};

        const result = setSectionFormatting(
            breakdown.value,
            resumeCopy.value,
            currentUser,
            formattingSectionFormat.value
        );

        if (result == null) return;

        breakdown.value = result.newBreakdown;
        formattingSectionFormat.value = result.newFormattingSectionFormat;
    };

    const marginsChangeHandler = () => {
        formattingMargins.value = {};
        const result = setMarginsSize(margins.value, breakdown.value);

        if (result?.formattingMargins != null) {
            formattingMargins.value = result.formattingMargins;
        }

        breakdown.value = result.breakdown;
        formattingMargins.value = result.formattingMargins;
    };

    const relevantInfoChangeHandler = () => {
        informationBp.value = {};

        const result = setRelevantInfoBp(breakdown.value, resumeCopy.value);

        breakdown.value = result.newBreakdown;
        informationBp.value = result.newInformationBp;
    };

    const experiencePopulationChecker = () => {
        experiencePopulation.value = setExperience(resumeCopy.value);
    };

    // SKILLS
    const skillsChangeHandler = async () => {
        let { jobs, bullets, newInternSkillsJobWarning } = getJobsAndBullets(
            resumeCopy.value
        );

        const currentJobs = jobs.map((job) => job.jobTitle);

        if (
            JSON.stringify(lastUsedJobsAndBullets.value.lastJobs) ===
            JSON.stringify(currentJobs) &&
            JSON.stringify(lastUsedJobsAndBullets.value.lastBullets) ===
            JSON.stringify(bullets)
        ) {
            if ("skills" in breakdown.value) {
                return;
            }

            breakdown.value = {
                ...breakdown.value,
                skills: { breakdownMessage: "notMet", score: 0 },
            };

            return;
        }

        lastUsedJobsAndBullets.value = {
            lastJobs: currentJobs,
            lastBullets: bullets,
        };

        skillsAddKeywords.value = {};
        internSkillsJobWarning.value = {};
        skillsNotPassedWarning.value = {};

        internSkillsJobWarning.value = newInternSkillsJobWarning;

        if (jobs?.length === 0 && bullets.length === 0) {
            breakdown.value = {
                ...breakdown.value,
                skills: {
                    breakdownMessage: "notMet",
                    score: 0,
                },
            };

            return;
        }

        try {
            const { newJobs, newSkillsNotPassedWarning } =
                await calculateKeywords(jobs, bullets);

            skillsNotPassedWarning.value = newSkillsNotPassedWarning;
            jobs = newJobs;

            const { newBreakdown, newSkillsAddKeywords } = setSkills(
                jobs,
                breakdown.value,
                skillsAddKeywords.value
            );

            breakdown.value = newBreakdown;
            skillsAddKeywords.value = newSkillsAddKeywords;
        } catch (err) {
            // TODO: Handler errors
        }
    };

    const educationInfoChangeHandler = () => {
        educationInfo.value = {};

        const result = setEducationScore(breakdown.value, resumeCopy.value);

        if (!result) {
            return;
        }

        breakdown.value = result.newBreakdownScoreEducation;
        educationInfo.value = result.newEducationInfo;
    };

    const experienceInfoChangeHandler = () => {
        experiencesInfo.value = {};

        const result = setExperienceScore(breakdown.value, resumeCopy.value);

        experiencesInfo.value = result.newExperiencesInfo;
        breakdown.value = result.newBreakdownScoreExperience;
    };

    const initScorer = () => {
        bulletPointsChangeHandler();
        relevantInfoChangeHandler();
        sectionsFormatingChangeHandler();
        experienceOrderChangeHandler();
        marginsChangeHandler();
        fontChangeHandler();
        relevantInfoExperienceChangeHandler();
        resumeContactChangeHandler();
        experiencePopulationChecker();
        skillsChangeHandler();
        educationInfoChangeHandler();
        experienceInfoChangeHandler();
        relevantInfoChangeHandler();
    };

    return {
        totalScore,
        getWarnings,
        breakdown,
        getScore,
        lastResumeName,
        lastResumeContact,
        margins,
        resumeCopy,
        textSizeCopy,
        bulletsIdCopy,
        nrOfPagesScorer,

        showResumeScorer,
        setShowResumeScorer,
        hideResumeScorer,

        showBreakdownModal,
        setShowBreakdown,
        hideBreakdown,

        relevantInfoExperienceChangeHandler,
        experienceOrderChangeHandler,
        sectionsFormatingChangeHandler,
        bulletPointsChangeHandler,

        relevantInfoChangeHandler,
        educationInfoChangeHandler,
        fontChangeHandler,
        resumeContactChangeHandler,
        marginsChangeHandler,

        experiencePopulationChecker,
        experienceInfoChangeHandler,

        skillsChangeHandler,

        initScorer,
    };
});

 
